import { createSlice } from '@reduxjs/toolkit';

import {
  AddMemberPostsPayload,
  AddPostsPayload,
  AddReactionPayload,
  RemoveReactionPayload,
  ReplacePostPayload,
  SetActivityEngagementPayload,
  SetApprovalCountPayload,
  SetApprovalMembersPayload,
  SetHashtagEngagementPayload,
  SetHashtagOccurrencesPayload,
  SetMemberPostsPayload,
  SetPollResultsPayload,
  SetPostHierarchyPayload,
  SetPostPayload,
  SetPostSearchResultsPayload,
  SetPostsPayload,
  SetProfilePayload,
  SetReactionsPayload,
  SetTribeTemplatePayload,
  SwitchReactionPayload,
  TribeState
} from './tribe.types';

export const initialState: TribeState = {
  tribeTemplate: undefined,
  posts: [],
  memberPosts: [],
  reactions: [],
  post: undefined,
  hashtagOccurrences: [],
  activityEngagement: [],
  hashtagEngagement: [],
  approvalCount: 0,
  approvalMembers: [],
  postSearchResults: undefined,
  postHierarchy: undefined,
  pollResults: undefined,
  profile: undefined
};

const TribeSlice = createSlice({
  name: 'tribe',
  initialState,
  reducers: {
    setTribeTemplate: (state, action: SetTribeTemplatePayload) => {
      state.tribeTemplate = action.payload;
    },
    setPosts: (state, action: SetPostsPayload) => {
      state.posts = action.payload;
    },
    setMemberPosts: (state, action: SetMemberPostsPayload) => {
      state.memberPosts = action.payload;
    },
    setPostSearchResults: (state, action: SetPostSearchResultsPayload) => {
      state.postSearchResults = action.payload;
    },
    setPostHierarchy: (state, action: SetPostHierarchyPayload) => {
      state.postHierarchy = action.payload;
    },
    setReactions: (state, action: SetReactionsPayload) => {
      state.reactions = action.payload;
    },
    addReaction: (state, action: AddReactionPayload) => {
      state.reactions.push(action.payload);

      const { postExternalReference, reaction } = action.payload;

      const foundPost = state.posts.find(({ externalReference }) => externalReference === postExternalReference);

      if (!foundPost?.reactionCount) throw new Error('Could not add reaction.');

      const foundReaction = foundPost.reactionCount.find(({ text }) => text === reaction);
      if (foundReaction) {
        if (foundReaction.count) {
          foundReaction.count++;
        } else {
          foundReaction.count = 1;
        }
      } else {
        foundPost.reactionCount?.push({ text: reaction, count: 1 });
      }
    },
    removeReaction: (state, action: RemoveReactionPayload) => {
      const { postExternalReference, reaction } = action.payload;

      state.reactions = state.reactions.filter((reactionInState) => {
        if (reactionInState.postExternalReference === postExternalReference) {
          return reactionInState.reaction !== reaction;
        }

        return true;
      });

      const foundPost = state.posts.find(({ externalReference }) => externalReference === postExternalReference);

      if (!foundPost?.reactionCount) throw new Error('Could not remove reaction.');

      const { reactionCount } = foundPost;

      const foundReactionIndex = reactionCount.findIndex(({ text }) => text === reaction);

      const reactionToPost = reactionCount[foundReactionIndex];

      if (!reactionToPost?.count) throw new Error('Could not remove reaction.');

      if (reactionToPost.count <= 1) {
        reactionCount.splice(foundReactionIndex, 1);
      } else {
        reactionToPost.count--;
      }
    },
    switchReaction: (state, action: SwitchReactionPayload) => {
      const { postExternalReference, oldReaction, newReaction } = action.payload;

      state.reactions = state.reactions.filter((reactionInState) => {
        if (reactionInState.postExternalReference === postExternalReference) {
          return reactionInState.reaction !== oldReaction;
        }

        return true;
      });

      const foundPost = state.posts.find(({ externalReference }) => externalReference === postExternalReference);

      if (!foundPost?.reactionCount) throw new Error('Could not switch reaction.');

      const { reactionCount } = foundPost;

      const foundReactionIndex = reactionCount.findIndex(({ text }) => text === oldReaction);

      const reactionToPost = reactionCount[foundReactionIndex];

      if (!reactionToPost?.count) throw new Error('Could not remove reaction.');

      if (reactionToPost.count <= 1) {
        reactionCount.splice(foundReactionIndex, 1);
      } else {
        reactionToPost.count--;
      }

      state.reactions.push({ postExternalReference, reaction: newReaction });

      const foundReaction = reactionCount.find(({ text }) => text === newReaction);
      if (foundReaction) {
        if (foundReaction.count) {
          foundReaction.count++;
        } else {
          foundReaction.count = 1;
        }
      } else {
        reactionCount.push({ text: newReaction, count: 1 });
      }
    },
    addPosts: (state, action: AddPostsPayload) => {
      const newPosts = action.payload;

      newPosts.forEach((post) => {
        const foundPost = state.posts.find(({ externalReference }) => externalReference === post.externalReference);

        if (foundPost) return;

        state.posts.push(post);
      });
    },
    addMemberPosts: (state, action: AddMemberPostsPayload) => {
      const newPosts = action.payload;

      newPosts.forEach((post) => {
        const foundPost = state.memberPosts.find(
          ({ externalReference }) => externalReference === post.externalReference
        );

        if (foundPost) return;

        state.memberPosts.push(post);
      });
    },
    replacePost: (state, action: ReplacePostPayload) => {
      const { page, posts } = action.payload;

      state.posts.splice(page, 1, ...posts);
    },
    replaceMemberPost: (state, action: ReplacePostPayload) => {
      const { page, posts } = action.payload;

      state.memberPosts.splice(page, 1, ...posts);
    },
    setPost: (state, action: SetPostPayload) => {
      state.post = action.payload;
    },
    setHashtagOccurrences: (state, action: SetHashtagOccurrencesPayload) => {
      state.hashtagOccurrences = action.payload;
    },
    setActivityEngagement: (state, action: SetActivityEngagementPayload) => {
      state.activityEngagement = action.payload;
    },
    setHashtagEngagement: (state, action: SetHashtagEngagementPayload) => {
      state.hashtagEngagement = action.payload;
    },
    setApprovalCount: (state, action: SetApprovalCountPayload) => {
      state.approvalCount = action.payload;
    },
    setApprovalMembers: (state, action: SetApprovalMembersPayload) => {
      state.approvalMembers = action.payload;
    },
    setPollResults: (state, action: SetPollResultsPayload) => {
      state.pollResults = action.payload;
    },
    setProfile: (state, action: SetProfilePayload) => {
      state.profile = action.payload;
    }
  }
});

export const {
  setTribeTemplate,
  setPosts,
  setMemberPosts,
  setReactions,
  addReaction,
  removeReaction,
  switchReaction,
  addPosts,
  addMemberPosts,
  replacePost,
  replaceMemberPost,
  setPost,
  setHashtagOccurrences,
  setActivityEngagement,
  setHashtagEngagement,
  setApprovalCount,
  setApprovalMembers,
  setPostSearchResults,
  setPostHierarchy,
  setPollResults,
  setProfile
} = TribeSlice.actions;

export default TribeSlice.reducer;
